import { useEffect, useRef } from 'react';
import Hls from 'hls.js';

interface HLSVideoProps {
  src: string;
  poster?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  className?: string;
  onError?: (error: any) => void;
}

export function HLSVideo({
  src,
  poster,
  autoPlay = false,
  muted = false,
  loop = false,
  playsInline = false,
  className = '',
  onError
}: HLSVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let hls: Hls | null = null;

    // Check if HLS is supported natively (Safari)
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
    } 
    // Check if HLS.js is supported for other browsers
    else if (Hls.isSupported()) {
      hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
      });
      
      hls.loadSource(src);
      hls.attachMedia(video);
      
      hls.on(Hls.Events.ERROR, (_event, data) => {
        console.warn('HLS.js error:', data);
        if (onError) onError(data);
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('HLS manifest loaded, starting video playback');
        if (autoPlay) {
          video.play().catch(console.warn);
        }
      });
    } 
    else {
      console.warn('HLS is not supported in this browser');
      if (onError) onError(new Error('HLS not supported'));
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [src, autoPlay, onError]);

  return (
    <video
      ref={videoRef}
      poster={poster}
      autoPlay={autoPlay}
      muted={muted}
      loop={loop}
      playsInline={playsInline}
      className={className}
      onError={(e) => {
        console.warn('Video element error:', e);
        if (onError) onError(e);
      }}
    >
      Your browser does not support the video tag.
    </video>
  );
}